import React, { useEffect, useRef, useState } from 'react';
import LeftIcon from '../../images/LeftIcon';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import { getLocations } from '../../services/inventoryCorrections.service';
import { validateNoSpecialChars, validateOnlyNumbersExpand } from '../../validation/ValidationInput';
import { createDevices, DevicePayload, getDetailDevices, updateDevices } from '../../services/devices.service';
import { getListSalePoint } from '../../services/salePoint.service';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import BackIcon from '../../images/BackIcon';
import { DropdownSearch } from '../../components/DropdownSearch';
import { useResponsive } from '../../hook/useResponsive';
import { useFormKeyboard } from '../../hook/useFormKeyboard';

export interface CountryResponse {
  name: string;
  id: string;
  isSelect: boolean;
}

interface ListCountrySelect {
  id: string;
}

const DeviceDetail = () => {
  const [language, setLanguage] = useState<any>(null);
  const limits = useFieldLimits(inventoryFormLimits('device'));
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const path = window.location.pathname;
  const id: string = path.split('/').pop() || '';
  const preLanguage = localStorage.getItem('language');
  const userId = localStorage.getItem('userId') || '';
  const companyId = localStorage.getItem('companyId') || '';
  const [country, setCountry] = useState<CountryResponse[]>([]);
  const [salePoint, setSalePoint] = useState<any[]>([]);
  const dropdownCountryRef = useRef<HTMLDivElement>(null);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [searchCountry, setSearchCountry] = useState("");
  const dropdownSalePointRef = useRef<HTMLDivElement>(null);
  const [isSalePointDropdownOpen, setIsSalePointDropdownOpen] = useState(false);
  const [searchSalePoint, setSearchSalePoint] = useState("");
  const { isMobile, getWidth } = useResponsive();
  // No autofocus: the first field is the location dropdown, which opens its list on focus.
  const kb = useFormKeyboard();
  const [formValues, setFormValues] = useState<DevicePayload>({
    deviceName: "",
    serialNo: "",
    macAddress: "",
    companyId: "",
    statusId: "0df6c6b8-505f-4824-a4ac-9f0647d7da7b",
    salePointId: "",
    locationId: "",
  });
  // device_key is backend-generated (DB default) and never edited from the UI;
  // held separately from the create/update payload and shown read-only.
  const [deviceKey, setDeviceKey] = useState<string>("");
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownCountryRef.current && !dropdownCountryRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
      if (dropdownSalePointRef.current && !dropdownSalePointRef.current.contains(event.target as Node)) {
        setIsSalePointDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  useEffect(() => {
    getListCountry();
    getSalePoints();
    if (id !== 'new') {
      fetchData();
    }
    const handleNewToken = async () => {
      getListCountry();
      getSalePoints();
      if (id !== 'new') {
        fetchData();
      }
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, [path]);


  const fetchData = async () => {
    webLoadingTrue();
    try {
      const response = await getDetailDevices(id);
      console.log("🚀 ~ fetchData ~ response:", response)
      setFormValues({
        deviceName: response.data.data.deviceName,
        serialNo: response.data.data.serialNo,
        macAddress: response.data.data.macAddress,
        companyId: response.data.data.companyId,
        statusId: response.data.data.statusId,
        locationId: response.data.data.locationId,
        salePointId: response.data.data.salePointId,
      });
      setDeviceKey(response.data.data.deviceKey || "");
      setSearchCountry(response.data.data.location.name || "");
      setSearchSalePoint(response.data.data.salePoint.salePointName || "");
    } catch (error) {
      console.error('Error loading terms:', error);
    } finally {
      webLoadingFalse();
    }
  };

  const getSalePoints = async () => {
    try {
      const response: any = await getListSalePoint(companyId, "0", 0, "");
      setSalePoint(response.data.data.salePoints);
    } catch (error) {
      console.error("Error loading terms:", error);
    }
  }

  const getListCountry = async () => {
    try {
      const response: any = await getLocations(userId, companyId, '', '', '');
      const listCountry: CountryResponse[] = response.data.data;
      setCountry(listCountry);
    } catch (error) {
      console.error('Error loading terms:', error);
    }
  };

  const handleSelectLocation = (location: CountryResponse) => {
    setFormValues(prev => ({ ...prev, locationId: location.id }))
    setSearchCountry(location.name);
    setIsCountryDropdownOpen(false);
  };

  const handleSelectSalePoint = (salePoint: any) => {
    setFormValues(prev => ({ ...prev, salePointId: salePoint?.id }))
    setSearchSalePoint(salePoint?.salePointName);
    setIsSalePointDropdownOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };


  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    // const newErrors: any = {};
    // if (Object.keys(newErrors).length > 0) {
    //   setErrors(newErrors);
    //   return;
    // }
    const payload: DevicePayload = { ...formValues, companyId };
    const newErrors = validateAgainstLimits(payload as any, limits, language, ['deviceName', 'serialNo', 'macAddress', 'locationId', 'salePointId']);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    webLoadingTrue();
    if (id === 'new') {
      try {
        const response: any = await createDevices(payload);
        if (response) {
          webNavigate('/' + preLanguage + '/devices');
          webLoadingFalse();
          showToast({
            message: language?.['Device Created !'] ?? 'Device Created !',
            type: 'success',
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: setErrors, titleFallback: language?.['Error creating device'] ?? 'Error creating device' });
      }
    } else {
      try {
        const response: any = await updateDevices(id, payload);
        if (response) {
          webNavigate('/' + preLanguage + '/devices');
          webLoadingFalse();
          showToast({
            message: language?.['Device Updated !'] ?? 'Device Updated !',
            type: 'success',
            duration: 20000,
          });
        }
      } catch (error: any) {
        webLoadingFalse();
        showApiError(error, { language, setFieldErrors: setErrors, titleFallback: language?.['Error updating device'] ?? 'Error updating device' });
      }
    }
  };

  return (
    <div {...kb.scopeProps}>
      <div className="bg-white">
        <div className="flex items-center ">
          <button
            type="button"
            aria-label={language?.['Back'] ?? 'Back'}
            className="w-6 h-6 flex items-center justify-center cursor-pointer"
            onClick={() => {
              webNavigate('/' + preLanguage + '/devices');
            }}
          >
            <BackIcon />
          </button>
          <p
            style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
            className="font-inter"
          >
            {language && language?.['Back'] ? language?.['Back'] : 'Back'}
          </p>
        </div>
      </div>
      {id === 'new' ? (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Add Device']
            ? language?.['Add Device']
            : 'Add Device'}
        </p>
      ) : (
        <p
          style={{
            margin: '20px 0px',
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: '28px',
            color: '#222525',
          }}
          className="font-inter"
        >
          {language && language?.['Edit Device']
            ? language?.['Edit Device']
            : 'Edit Device'}
        </p>
      )}
      <form style={{ height: 'calc(100% - 163px)' }}>
        <div
          className="flex items-center"
          style={{ flexWrap: 'wrap', gap: 24 }}
        >
          <div className="space-y-2" style={{ width: isMobile ? '100%' : 'calc(33% - 12px)' }}>
            <div className="flex items-center justify-between">
              <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>{language && language?.["Location"] ? language?.["Location"] : "Location"}</p>
            </div>
            <div className="w-full">
              <DropdownSearch
                refKey="type"
                refs={{ type: dropdownCountryRef }}
                openStates={{ type: isCountryDropdownOpen }}
                open={(key) => key === "type" && setIsCountryDropdownOpen(true)}
                close={(key) => key === "type" && setIsCountryDropdownOpen(false)}
                data={country}
                labelExtractor={(item) => item.name}
                value={searchCountry}
                setValue={setSearchCountry}
                onSelect={handleSelectLocation}
                error={errors.locationId}
                borderNone={false}
              />
            </div>
          </div>
          <div className="space-y-2" style={{ width: isMobile ? '100%' : 'calc(33% - 12px)' }}>
            <div className="flex items-center">
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: '#4A5353', fontWeight: '500' }}
              >
                {language && language?.['Device Name'] ? language?.['Device Name'] : 'Device Name'}
              </p>
              <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                {language && language?.['*'] ? language?.['*'] : '*'}
              </p>
            </div>
            <div className="w-full">
              <input
                type="text"
                name="deviceName"
                {...limitProps(limits, 'deviceName')}
                value={formValues.deviceName}
                onChange={(e: any) => {
                  handleChange(e);
                }}
                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
              />
              <FieldCounter value={formValues.deviceName} max={limits.deviceName?.maxLength} />
            </div>
            {errors.deviceName && <p className="error">{errors.deviceName}</p>}
          </div>
          <div className="space-y-2" style={{ width: isMobile ? '100%' : 'calc(33% - 12px)' }}>
            <div className="flex items-center justify-between">
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: '#4A5353', fontWeight: '500' }}
              >
                {language && language?.['Serial No'] ? language?.['Serial No'] : 'Serial No'}
              </p>
              {/* <p
                      className="text-[14px] font-inter leading-[20px]"
                      style={{ color: '#898989' }}
                    >
                      {language && language?.['Required'] ? language?.['Required'] : 'Required'}
                    </p> */}
            </div>
            <div className="w-full">
              <input
                type="text"
                name="serialNo"
                {...limitProps(limits, 'serialNo')}
                value={formValues.serialNo}
                onChange={(e: any) => {
                  handleChange(e);
                }}
                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
              />
              <FieldCounter value={formValues.serialNo} max={limits.serialNo?.maxLength} />
            </div>
            {errors.serialNo && <p className="error">{errors.serialNo}</p>}
          </div>
          <div className="space-y-2" style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
            <div className="flex items-center justify-between">
              <p
                className="text-[14px] font-inter leading-[20px]"
                style={{ color: '#4A5353', fontWeight: '500' }}
              >
                {language && language?.['MAC Address'] ? language?.['MAC Address'] : 'MAC Address'}
              </p>
              {/* <p
                      className="text-[14px] font-inter leading-[20px]"
                      style={{ color: '#898989' }}
                    >
                      {language && language?.['Required'] ? language?.['Required'] : 'Required'}
                    </p> */}
            </div>
            <div className="w-full">
              <input
                type="text"
                name="macAddress"
                {...limitProps(limits, 'macAddress')}
                value={formValues.macAddress}
                onChange={(e: any) => {
                  handleChange(e);
                }}
                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
              />
              <FieldCounter value={formValues.macAddress} max={limits.macAddress?.maxLength} />
            </div>
            {errors.macAddress && <p className="error">{errors.macAddress}</p>}
          </div>
          {id !== 'new' && deviceKey && (
            <div className="space-y-2" style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
              <div className="flex items-center justify-between">
                <p
                  className="text-[14px] font-inter leading-[20px]"
                  style={{ color: '#4A5353', fontWeight: '500' }}
                >
                  {language && language?.['Device Key'] ? language?.['Device Key'] : 'Device Key'}
                </p>
              </div>
              <div className="w-full">
                {/* Backend-generated key — read-only, kept copyable (not disabled). */}
                <input
                  type="text"
                  name="deviceKey"
                  value={deviceKey}
                  readOnly
                  aria-readonly="true"
                  className={`text-[14px] font-inter placeholder:text-placeholder text-[#898989] border border-[#EBEBED] p-3 rounded-xl outline-none focus-none bg-[#F5F5F5] w-full cursor-not-allowed`}
                />
              </div>
            </div>
          )}
          <div className="space-y-2" style={{ width: isMobile ? '100%' : 'calc(50% - 12px)' }}>
            <div className="flex items-center justify-between">
              <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>{language && language?.["Sale Point"] ? language?.["Sale Point"] : "Sale Point"}</p>
            </div>
            <div className="w-full">
              <DropdownSearch
                refKey="type"
                refs={{ type: dropdownSalePointRef }}
                openStates={{ type: isSalePointDropdownOpen }}
                open={(key) => key === "type" && setIsSalePointDropdownOpen(true)}
                close={(key) => key === "type" && setIsSalePointDropdownOpen(false)}
                data={salePoint}
                labelExtractor={(item) => item.salePointName}
                value={searchSalePoint}
                setValue={setSearchSalePoint}
                onSelect={handleSelectSalePoint}
                error={errors.salePointId}
                borderNone={false}
              />
            </div>
            {errors.salePointId && <p className="error">{errors.salePointId}</p>}
          </div>
        </div>
      </form>
      <div
        className="w-full flex items-center justify-end shadow_button relative"
        style={{ gap: "12px", paddingTop: "24px", }}
      >
        <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: isMobile ? 'calc(100vw + 32px)' : "calc(-256px + 48px + 100vw)" }}></div>
        <button
          type="button"
          onClick={() => { webNavigate("/" + preLanguage + '/devices') }}
          style={{
            padding: '10px 24px',
            borderRadius: '30px',
            color: '#222525',
            fontSize: '14px',
            fontWeight: '500',
            border: '1px solid #C9D7D8',
          }}
          className="font-inter cursor-pointer"
        >
          {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
        </button>
        <button type="button" style={{
          padding: '10px 24px',
          borderRadius: '30px',
          color: '#FFFFFF',
          fontSize: '14px',
          fontWeight: '500',
          backgroundColor: '#0F7679',
        }}
          className="font-inter cursor-pointer"
          {...kb.primaryProps(handleSubmit)}
        >
          <p className="text-[14px] leading-[22px] font-inter">{language && language?.["Save"] ? language?.["Save"] : "Save"}</p>
        </button>
      </div>
    </div>
  );
};
export default DeviceDetail;
